import { useNavigate, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getCategoryById, getRecommended, getSoftwareById, formatDownloads, platformIcons } from '@/utils/catalog';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SoftwareCard from '@/components/feature/SoftwareCard';
import EmptyState from '@/components/base/EmptyState';
import ScreenshotGallery from './components/ScreenshotGallery';
import DownloadPanel from './components/DownloadPanel';

export default function DetailPage() {
  const { softwareId = '' } = useParams();
  const navigate = useNavigate();
  const item = getSoftwareById(softwareId);
  const category = item ? getCategoryById(item.categoryId) : undefined;

  usePageMeta(
    item ? `${item.name}下载 - ${category?.name ?? '软件'} - 看片软件` : '软件详情 - 看片软件',
    item ? `${item.name}（${item.enName}）${item.version}：${item.summary}` : '查看看片软件的详细介绍、功能特色与下载地址。',
  );

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="该软件信息暂时不可用"
          description="你访问的软件可能已下架或链接有误，返回首页浏览其它推荐软件吧。"
          actionLabel="返回首页"
          onAction={() => navigate('/')}
        />
      </div>
    );
  }

  const related = getRecommended(item, 3);
  const fullStars = Math.round(item.rating);

  const infoRows = [
    { label: '开发者', value: item.developer, icon: 'ri-team-line' },
    { label: '发布时间', value: item.releaseDate, icon: 'ri-calendar-line' },
    { label: '软件版本', value: item.version, icon: 'ri-git-branch-line' },
    { label: '文件大小', value: item.size, icon: 'ri-hard-drive-2-line' },
  ];

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 pb-24 md:px-6 md:py-10 md:pb-10">
      <div className="mb-6">
        <Breadcrumb
          items={[
            { label: '软件分类', to: '/search' },
            { label: category?.name ?? '未分类', to: category ? `/category/${category.id}` : undefined },
            { label: item.name },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-6">
          <header className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="relative h-56 w-full bg-secondary-100 md:h-72">
              <img
                src={item.cover}
                alt={`${item.name} ${item.enName} 影视播放软件`}
                title={`${item.name} - 看片软件下载`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="p-5 md:p-6">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">{item.type}</span>
                {category && (
                  <button
                    type="button"
                    onClick={() => navigate(`/category/${category.id}`)}
                    className="cursor-pointer rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    {category.name}
                  </button>
                )}
              </div>

              <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">{item.name}</h1>
              <p className="mt-1 text-sm text-foreground-400">{item.enName}</p>

              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-accent-600">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <i key={i} className={i < fullStars ? 'ri-star-fill' : 'ri-star-line text-accent-300'}></i>
                  ))}
                  <span className="ml-1 font-semibold">{item.rating.toFixed(1)}</span>
                </span>
                <span className="flex items-center gap-1 text-foreground-500">
                  <i className="ri-download-2-line"></i>
                  {formatDownloads(item.downloads)}次下载
                </span>
                <span className="flex items-center gap-1 text-foreground-500">
                  <i className="ri-calendar-check-line"></i>
                  更新于 {item.updateDate}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {item.platforms.map((p) => (
                  <span key={p} className="flex items-center gap-1.5 rounded-md border border-background-200 px-3 py-1.5 text-xs text-foreground-600">
                    <i className={`${platformIcons[p]} text-sm text-primary-600`}></i>
                    {p}
                  </span>
                ))}
              </div>

              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{item.summary}</p>

              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="#download-panel"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-download-line text-base"></i>前往下载
                </a>
                {category && (
                  <button
                    type="button"
                    onClick={() => navigate(`/category/${category.id}`)}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-arrow-left-line"></i>返回分类
                  </button>
                )}
              </div>
            </div>
          </header>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-sparkling-2-line text-primary-600"></i>功能特色
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {item.features.map((f, i) => (
                <div key={f.title} className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${['ri-flashlight-fill', 'ri-shield-star-fill', 'ri-rocket-2-fill'][i % 3]} text-lg`}></i>
                  </span>
                  <h3 className="mb-1.5 text-sm font-semibold text-foreground-900">{f.title}</h3>
                  <p className="text-xs leading-relaxed text-foreground-500">{f.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-file-text-line text-primary-600"></i>软件介绍
            </h2>
            <p className="text-sm leading-loose text-foreground-600">{item.description}</p>
          </section>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <ScreenshotGallery name={item.name} screenshots={item.screenshots} />
          </div>

          <DownloadPanel item={item} />

          {related.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
                <i className="ri-movie-2-line text-primary-600"></i>相关推荐
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <SoftwareCard key={r.id} item={r} />
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="mb-4 text-sm font-semibold text-foreground-900">软件信息</h3>
            <ul className="flex flex-col gap-3">
              {infoRows.map((row) => (
                <li key={row.label} className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2 text-foreground-500">
                    <i className={`${row.icon} text-base text-secondary-500`}></i>
                    {row.label}
                  </span>
                  <span className="font-medium text-foreground-900">{row.value}</span>
                </li>
              ))}
              <li className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 text-foreground-500">
                  <i className="ri-device-line text-base text-secondary-500"></i>支持平台
                </span>
                <span className="font-medium text-foreground-900">{item.platforms.join(' / ')}</span>
              </li>
            </ul>

            <a
              href="#download-panel"
              className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-download-2-line"></i>获取下载地址
            </a>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-home-5-line"></i>返回首页
            </button>
          </div>
        </aside>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 flex items-center gap-3 border-t border-background-200 bg-background-50 p-3 md:hidden">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground-900">{item.name}</p>
          <p className="text-xs text-foreground-400">{item.version} · {item.size}</p>
        </div>
        <a
          href="#download-panel"
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-foreground-950"
        >
          <i className="ri-download-line"></i>下载
        </a>
      </div>
    </div>
  );
}